/**
 * "Any of these" / "All of these" over audience filters — the v3 lens
 * contract's `or` and `and`, as a block.
 *
 * Pins inside a Page Layer or inside another group, and holds "Only show"
 * blocks and groups. Never a boost: boosts are not boolean, they blend.
 *
 * Its own shape rather than the Logic node with a flag, deliberately. Logic's
 * `canBind` accepts every match condition, and the two vocabularies must not
 * mix: a match leaf inside an audience group would compile into nothing the
 * serve path can read, and an audience leaf inside a match group would take
 * the feed dark. Refusing the drop is better than refusing the save.
 *
 * Why this exists: v2's filters were conjunctive, and `follows2` excludes
 * people you already follow, so the one live feed that asked for "follows AND
 * follows2 AND community" was empty by construction and served unfiltered to
 * everyone. The creator meant "or".
 */

import { BrainCircuitIcon } from "lucide-react";
import { useCallback } from "react";
import {
  Editor,
  RecordProps,
  T,
  TLShape,
  TLShapeUtilCanBindOpts,
  useValue,
} from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import { CanvasSimpleSelect } from "@/components/canvas-editor/components/Inputs";
import {
  AUDIENCE_BLOCK_MIN_W,
  groupDepthOf,
  rootLayerOf,
} from "@/components/canvas-editor/layers";
import {
  NodeDocsDescription,
  NodeDocsHeader,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
  dropTargetId,
} from "@/components/canvas-editor/shapes/bindable";
import {
  audienceGroupChildShapes,
  AudienceLogicProps,
  AudienceLogicShape,
  audienceLogicType,
  Dimensions,
  isAudienceLogicShape,
  isLayerShape,
  layerType,
  LogicalOp,
  logicalOpOptions,
  logicalOps,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { MAX_AUDIENCE_FILTER_DEPTH } from "@/lib/lens";
import { cn } from "@/lib/utils";

function AudienceLogicDocs() {
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>Audience Group</NodeDocsHeader>
      <NodeDocsDescription>
        Combine several &ldquo;Only show posts from&rdquo; blocks. With{" "}
        <strong>any of these</strong>, a post is kept when its author is in
        any one of them — &ldquo;people I follow or my communities&rdquo;.
        With <strong>all of these</strong>, the author must be in every one.
      </NodeDocsDescription>
      <NodeDocsDescription>
        Groups nest up to three deep. Boosts do not go inside a group: they
        blend rather than decide.
      </NodeDocsDescription>
    </div>
  );
}

export class AudienceLogicTool extends ToolStateNode {
  static override id = audienceLogicType;

  static override uiToolDef = (editor: Editor) => ({
    id: audienceLogicType,
    icon: BrainCircuitIcon,
    label: AudienceLogicShapeUtil.docs.title,
    defaultColor: AudienceLogicShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(audienceLogicType),
    docs: () => <AudienceLogicDocs />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;
    this.editor.createShape({
      type: audienceLogicType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });
    this.editor.setCurrentTool("select");
  }
}

export class AudienceLogicShapeUtil extends BindableUtil<AudienceLogicShape> {
  static override migrations = createMigrations(audienceLogicType);
  static override type = audienceLogicType;

  static override docs = {
    defaultColor: "plum" as const,
    title: "Audience Group",
    description: "Any or all of several audience filters.",
  };

  override getType() {
    return audienceLogicType;
  }

  // No `resultState` — see audience-filter.tsx.
  static override props: RecordProps<AudienceLogicShape> = {
    w: T.number,
    minW: T.number,
    h: T.number,
    isCollapsed: T.boolean.optional(),
    isReadOnly: T.boolean.optional(),
    metadata: metadataType,
    type: T.literalEnum(...logicalOps),
    padding: T.object({
      top: T.number,
      left: T.number,
      right: T.number,
      bottom: T.number,
      gap: T.number,
    }),
  };

  override getDefaultProps() {
    return {
      w: AUDIENCE_BLOCK_MIN_W,
      minW: AUDIENCE_BLOCK_MIN_W,
      h: 101,
      // "Any" is the default because it is the case v2 could not express and
      // the reason the block exists; "all" is what two loose filters already do.
      type: "or" as LogicalOp,
      padding: {
        top: 98,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      metadata: undefined,
    };
  }

  /** Into a layer or another group; holds filters, groups and notes — never a boost. */
  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<AudienceLogicShape>) {
    if (bindingType === "arrow") return true;
    if (bindingType !== "layout") return false;
    // As a container:
    if (fromShapeType === audienceLogicType) {
      return audienceGroupChildShapes.includes(toShapeType);
    }
    // As a child:
    return fromShapeType === layerType && toShapeType === audienceLogicType;
  }

  /** Refuse a drop that would nest groups past the contract's depth cap. */
  override canPlaceInShape(container: TLShape, shape: TLShape) {
    if (!super.canPlaceInShape(container, shape)) return false;
    if (!isLayerShape(container) && !isAudienceLogicShape(container)) return false;
    // This group would sit one level below its container; its own subtree adds more.
    return groupDepthOf(this.editor, container) + 1 + subtreeDepth(this.editor, shape) <=
      MAX_AUDIENCE_FILTER_DEPTH;
  }

  override component(shape: AudienceLogicShape) {
    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);
    return (
      <AudienceLogic
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={Boolean(rootLayerOf(this.editor, shape))}
        isSelected={isSelected}
      />
    );
  }
}

/** How many group levels sit inside this shape, counting itself as 0. */
function subtreeDepth(editor: Editor, shape: TLShape): number {
  const children = editor
    .getBindingsFromShape(shape, "layout")
    .map((b) => editor.getShape(b.toId))
    .filter((s): s is TLShape => Boolean(s) && isAudienceLogicShape(s!));
  if (children.length === 0) return 0;
  return 1 + Math.max(...children.map((c) => subtreeDepth(editor, c)));
}

function AudienceLogic({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected?: boolean;
  isBound: boolean;
  shape: AudienceLogicShape;
  className?: string;
  updateShape: (shape: AudienceLogicShape, props: Partial<AudienceLogicProps>) => void;
}) {
  const variant = shape.props.type === "and" ? "blue" : "plum";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => updateShape(shape, dimensions),
    [shape, updateShape],
  );
  const dropTarget = useValue("audience-logic-drop", () => dropTargetId.get(), []);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        pattern={shape.props.metadata?.pattern}
        className={cn(
          className,
          dropTarget === shape.id && "rounded-lg shadow-[0_0_0_4px_#0588F0]",
        )}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={shape.props.isCollapsed}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{ ...AudienceLogicShapeUtil.docs, variant }}
        />
        <BlockBody>
          <div className="flex flex-1 p-1">
            <CanvasSimpleSelect
              aria-label="Audience group operator"
              value={shape.props.type}
              className="flex-1"
              options={logicalOpOptions}
              onValueChange={(v) => updateShape(shape, { type: v as LogicalOp })}
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
